'use client';

import { createRoot, type Root } from 'react-dom/client';

import { CartSheetWidget, useCart, type CartItem } from '@/features/cart';
import { createWidgetMount } from '@/shared/utils/widget-mount';
import {
  DEFAULT_CART_SHEET_WIDGET_PROPS,
  OBSERVED_CART_SHEET_WIDGET_ATTRIBUTES,
  readCartSheetWidgetProps,
} from './cart-sheet-element-props';
import type { CartCommandEventName, CartEventName } from './types';

const ADD_COMMAND: CartCommandEventName = 'adipa-cart:add';
const HYDRATE_COMMAND: CartCommandEventName = 'adipa-cart:hydrate';
const REMOVE_COMMAND: CartCommandEventName = 'adipa-cart:remove';
const CLEAR_COMMAND: CartCommandEventName = 'adipa-cart:clear';
const OPEN_COMMAND: CartCommandEventName = 'adipa-cart:open';
const CLOSE_COMMAND: CartCommandEventName = 'adipa-cart:close';
const TOGGLE_COMMAND: CartCommandEventName = 'adipa-cart:toggle';

const CART_COMMAND_EVENTS: CartCommandEventName[] = [
  ADD_COMMAND,
  HYDRATE_COMMAND,
  REMOVE_COMMAND,
  CLEAR_COMMAND,
  OPEN_COMMAND,
  CLOSE_COMMAND,
  TOGGLE_COMMAND,
];

function dispatchCartEvent(element: HTMLElement, name: CartEventName, detail?: unknown) {
  element.dispatchEvent(
    new CustomEvent(name, {
      detail,
      bubbles: true,
      composed: true,
    }),
  );
}

function isCartItem(value: unknown): value is CartItem {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.slug === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.image_url === 'string' &&
    typeof candidate.price === 'number' &&
    typeof candidate.currency === 'string'
  );
}

/** Descarta ítems repetidos por `id` (claves duplicadas rompen el render). */
function dedupeById(items: CartItem[]): CartItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function readHydrateDetail(detail: unknown): { items: CartItem[]; open: boolean } | null {
  // Se filtran los ítems inválidos en vez de descartar el lote completo: un
  // único producto mal formado no debe impedir que el resto llegue al drawer.
  if (Array.isArray(detail)) {
    return { items: dedupeById(detail.filter(isCartItem)), open: false };
  }

  if (!detail || typeof detail !== 'object') return null;

  const candidate = detail as Record<string, unknown>;
  if (!Array.isArray(candidate.items)) return null;

  return {
    items: dedupeById(candidate.items.filter(isCartItem)),
    open: candidate.open === true,
  };
}

export class CartSheetWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_CART_SHEET_WIDGET_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;
  private unsubscribeStore: (() => void) | null = null;
  private commandHandlers = new Map<string, EventListener>();

  get country(): string {
    return this.getAttribute('platform-country') ?? DEFAULT_CART_SHEET_WIDGET_PROPS.country;
  }

  set country(value: string) {
    this.setAttribute('platform-country', value);
  }

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.bindCommandListeners();
    this.subscribeToStore();
    this.scheduleRender();
  }

  disconnectedCallback() {
    this.unbindCommandListeners();
    this.unsubscribeStore?.();
    this.unsubscribeStore = null;

    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;
      if (this.isConnected || this.root !== root) return;
      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  private scheduleRender() {
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private subscribeToStore() {
    if (this.unsubscribeStore) return;

    let previousItems = useCart.getState().items;
    this.unsubscribeStore = useCart.subscribe((state) => {
      if (state.items === previousItems) return;
      previousItems = state.items;
      dispatchCartEvent(this, 'adipa-cart-changed', {
        items: state.items,
        count: state.items.length,
      });
    });
  }

  private bindCommandListeners() {
    if (typeof window === 'undefined') return;
    if (this.commandHandlers.size > 0) return;

    const register = (name: CartCommandEventName, handler: EventListener) => {
      window.addEventListener(name, handler);
      this.commandHandlers.set(name, handler);
    };

    register(ADD_COMMAND, (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!isCartItem(detail)) return;
      useCart.getState().add(detail);
    });

    register(HYDRATE_COMMAND, (event: Event) => {
      const detail = readHydrateDetail((event as CustomEvent).detail);
      if (!detail) return;
      useCart.getState().hydrate(detail.items);
      if (detail.open) useCart.getState().setIsOpen(true);
    });

    register(REMOVE_COMMAND, (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const id = typeof detail === 'string' ? detail : (detail as { id?: string })?.id;
      if (typeof id !== 'string') return;
      useCart.getState().remove(id);
    });

    register(CLEAR_COMMAND, () => {
      useCart.getState().clear();
    });

    register(OPEN_COMMAND, () => {
      useCart.getState().setIsOpen(true);
    });

    register(CLOSE_COMMAND, () => {
      useCart.getState().setIsOpen(false);
    });

    register(TOGGLE_COMMAND, () => {
      const { isOpen, setIsOpen } = useCart.getState();
      setIsOpen(!isOpen);
    });
  }

  private unbindCommandListeners() {
    if (typeof window === 'undefined') return;
    for (const event of CART_COMMAND_EVENTS) {
      const handler = this.commandHandlers.get(event);
      if (handler) window.removeEventListener(event, handler);
    }
    this.commandHandlers.clear();
  }

  private renderWidget() {
    if (!this.root) return;

    const props = readCartSheetWidgetProps(this);
    const { items } = useCart.getState();

    dispatchCartEvent(this, 'adipa-cart-ready', {
      country: props.country,
      count: items.length,
    });

    this.root.render(
      <CartSheetWidget
        checkoutUrl={props.checkoutUrl}
        emptyStateUrl={props.emptyStateUrl}
        notice={props.notice}
        noticeHref={props.noticeHref}
        vnPhase={props.vnPhase}
        savedIds={props.savedIds}
        onCheckoutClick={(cartItems) =>
          dispatchCartEvent(this, 'adipa-cart-checkout-click', {
            items: cartItems,
            count: cartItems.length,
          })
        }
        onItemNavigate={(item) =>
          dispatchCartEvent(this, 'adipa-cart-item-click', {
            id: item.id,
            slug: item.slug,
            title: item.title,
          })
        }
        onItemRemove={(item) =>
          dispatchCartEvent(this, 'adipa-cart-item-remove', {
            id: item.id,
            slug: item.slug,
            title: item.title,
          })
        }
      />,
    );
  }
}
